import {
  activeSessionsResponseSchema,
  formatDateTime,
  formatRelative,
  type ActiveSession,
} from '@fluxus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, LogOut, Monitor, ShieldCheck, Smartphone } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { describeDevice } from '@/lib/auth/device';
import { useAuth } from '@/lib/auth/session';

const SESSIONS_KEY = ['auth', 'sessions'] as const;

/** Configurações › Minha conta › Sessões ativas: onde a conta está aberta, com opção de encerrar. */
export function SessoesAtivasPage() {
  const { client, logout } = useAuth();
  const queryClient = useQueryClient();
  // A sessão do diálogo fica guardada depois de fechar, para o texto não sumir durante a animação.
  const [pending, setPending] = useState<ActiveSession | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const listRef = useRef<HTMLElement>(null);
  const ask = (session: ActiveSession) => {
    setPending(session);
    setConfirmOpen(true);
  };

  const query = useQuery({
    queryKey: SESSIONS_KEY,
    queryFn: ({ signal }) =>
      client.request('/auth/sessions', { schema: activeSessionsResponseSchema, signal }),
  });

  const end = useMutation({
    meta: { handlesError: true },
    mutationFn: async (session: ActiveSession) => {
      // Encerrar a sessão deste aparelho é sair (limpa o aparelho e volta ao login).
      if (session.current) return logout();
      await client.request(`/auth/sessions/${session.id}`, { method: 'DELETE' });
    },
    onSuccess: async (_data, session) => {
      setConfirmOpen(false);
      if (session.current) return;
      toast.success('Sessão encerrada', {
        description: `${describeDevice(session.platform, session.userAgent)} precisará entrar de novo.`,
      });
      await queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
      // A linha que abriu o diálogo saiu da lista: o foco vai para a lista (e não se perde no <body>).
      listRef.current?.focus();
    },
    onError: (err) => {
      toast.error('Não foi possível encerrar a sessão', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  const items = query.data?.items ?? [];
  const sorted = [...items].sort((a, b) => Number(b.current) - Number(a.current));

  return (
    <PageContainer className="max-w-3xl">
      <PageHeader
        title="Sessões ativas"
        description="Aparelhos e navegadores em que sua conta está aberta. Encerre qualquer um que você não reconheça ou não use mais."
      />

      <section ref={listRef} tabIndex={-1} aria-label="Sessões" className="mt-6 outline-none">
        {query.isPending ? (
          <LoadingState rows={3} label="Carregando sessões" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
        ) : sorted.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="Nenhuma sessão ativa"
            description="Quando você entrar em um aparelho, ele aparece aqui."
          />
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface">
            {sorted.map((session) => (
              <SessionRow key={session.id} session={session} onEnd={() => ask(session)} />
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={pending?.current ? 'Sair deste aparelho?' : 'Encerrar esta sessão?'}
        description={
          pending?.current
            ? 'Você vai sair da sua conta neste aparelho e voltar para a tela de login.'
            : `${pending ? describeDevice(pending.platform, pending.userAgent) : ''} será desconectado e precisará entrar de novo com e-mail e senha.`
        }
        confirmLabel={pending?.current ? 'Sair' : 'Encerrar sessão'}
        destructive
        busy={end.isPending}
        onConfirm={() => pending && end.mutate(pending)}
      />
    </PageContainer>
  );
}

const PLATFORM_ICON = { WEB: Globe, DESKTOP: Monitor, ANDROID: Smartphone, IOS: Smartphone } as const;

function SessionRow({ session, onEnd }: { session: ActiveSession; onEnd: () => void }) {
  const Icon = PLATFORM_ICON[session.platform];
  const device = describeDevice(session.platform, session.userAgent);
  return (
    <li className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4 md:px-5">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Icon className="size-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-bold">{device}</h2>
            {session.current ? <Badge variant="brand">Este aparelho</Badge> : null}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {session.current ? 'Em uso agora' : `Último uso ${formatRelative(session.lastUsedAt)}`}
            {session.ip ? ` · IP ${session.ip}` : null}
          </p>
          <p className="text-sm text-muted-foreground">Entrou em {formatDateTime(session.createdAt)}</p>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="self-start sm:self-center"
        onClick={onEnd}
        aria-label={session.current ? 'Sair deste aparelho' : `Encerrar sessão: ${device}`}
      >
        <LogOut aria-hidden />
        {session.current ? 'Sair' : 'Encerrar'}
      </Button>
    </li>
  );
}
